import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Boxes,
  Building2,
  CalendarDays,
  Check,
  Copy,
  FolderKanban,
  History,
  KeyRound,
  Loader2,
  MessageSquare,
  Package,
  Pencil,
  Receipt,
  Send,
  Trash2,
  UserSquare2,
  Wallet,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { FileUploadZone } from "@/modules/media/components/FileUploadZone";
import { ExternalCompanyPicker } from "@/components/shared/ExternalCompanyPicker";
import { PhaseStagePicker } from "@/components/shared/PhaseStagePicker";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";
import { useBudgetList } from "@/modules/budgets/hooks/useBudgets";
import { useVaultList } from "@/modules/vault/hooks/useVault";
import { useAssetList } from "@/modules/assets/hooks/useAssets";
import { useContactList } from "@/modules/contacts/hooks/useContacts";
import { ReimbursementCard } from "../components/ReimbursementCard";
import {
  useAddExpenseComment,
  useApproveExpense,
  useDeleteExpense,
  useDeleteExpenseComment,
  useExpense,
  useExpenseComments,
  useRejectExpense,
  useUpdateExpense,
} from "../hooks/useExpenses";
import { CurrencyAmount, formatCurrency } from "../components/CurrencyAmount";
import { NewExpenseDrawer } from "../components/NewExpenseDrawer";

const BORDER = "hsl(210,30%,85%)";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function StatusPill({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    pending: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "Pending" },
    approved: {
      bg: "hsl(142,55%,92%)",
      fg: "hsl(142,72%,28%)",
      label: "Approved",
    },
    rejected: { bg: "hsl(0,84%,95%)", fg: "hsl(0,72%,45%)", label: "Rejected" },
  };
  const s = styles[status] ?? styles.pending;
  return (
    <span
      className="inline-flex items-center rounded-full px-3 py-1 text-[12px] font-semibold"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}


export default function ExpenseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: expense, isLoading } = useExpense(id ?? "");
  const updateExpense = useUpdateExpense(id ?? "");
  const approveExpense = useApproveExpense();
  const rejectExpense = useRejectExpense();
  const deleteExpense = useDeleteExpense();
  const commentsQuery = useExpenseComments(id ?? "");
  const addComment = useAddExpenseComment(id ?? "");
  const deleteComment = useDeleteExpenseComment(id ?? "");

  const [editMode, setEditMode] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [tab, setTab] = useState<"comments" | "attachments">("comments");
  const [commentText, setCommentText] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [toast, setToast] = useState<string | null>(null);

  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editSubcategory, setEditSubcategory] = useState("");
  const [editPaymentMethod, setEditPaymentMethod] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editCurrency, setEditCurrency] = useState("AED");
  const [editRate, setEditRate] = useState("1");
  const [editReceiptUrl, setEditReceiptUrl] = useState("");
  const [editVendorCompanyId, setEditVendorCompanyId] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editCompanyId, setEditCompanyId] = useState("");
  const [editProductId, setEditProductId] = useState("");
  const [editProjectId, setEditProjectId] = useState("");
  const [editPhaseId, setEditPhaseId] = useState<string | null>(null);
  const [editStageId, setEditStageId] = useState<string | null>(null);
  const [editMeetingId, setEditMeetingId] = useState("");
  const [editBudgetId, setEditBudgetId] = useState("");
  const [editAccessVaultId, setEditAccessVaultId] = useState("");
  const [editAssetId, setEditAssetId] = useState("");
  const [editVendorContactId, setEditVendorContactId] = useState("");
  const [editPaidById, setEditPaidById] = useState("");
  const [editIsReimbursable, setEditIsReimbursable] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data: categoryItems = [] } = useCategoryItems("expense_category");
  const { data: subcategoryItems = [] } = useCategoryItems(
    "expense_subcategory",
  );
  const { data: paymentItems = [] } = useCategoryItems(
    "expense_payment_method",
  );
  const { data: usersResult } = useUserList({
    page: 1,
    limit: 100,
    status: "ACTIVE",
  });
  const allUsers = usersResult?.data ?? [];

  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 100,
    operationalOnly: true,
  });
  const companies = companiesResult?.data ?? [];

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 100,
    companyId: editCompanyId || undefined,
  });
  const products = productsResult?.data ?? [];

  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 100,
    companyId: editCompanyId || undefined,
    productId: editProductId || undefined,
  });
  const projects = projectsResult?.data ?? [];

  const { data: meetingsResult } = useMeetingList({
    page: 1,
    limit: 100,
    projectId: editProjectId || undefined,
  });
  const meetings = meetingsResult?.data ?? [];

  const { data: budgetsResult } = useBudgetList({
    page: 1,
    limit: 100,
    status: "active",
    companyId: editCompanyId || undefined,
    productId: editProductId || undefined,
    projectId: editProjectId || undefined,
  });
  const budgets = budgetsResult?.data ?? [];

  const { data: vaultResult } = useVaultList({ page: 1, limit: 100 });
  const vaultEntries = vaultResult?.data ?? [];
  const { data: assetsResult } = useAssetList({ page: 1, limit: 100 });
  const assets = assetsResult?.data ?? [];
  const { data: contactsResult } = useContactList({ page: 1, limit: 100 });
  const vendorContacts = useMemo(
    () =>
      (contactsResult?.data ?? []).filter(
        (c) =>
          c.id === editVendorContactId ||
          (c.status === "Active" && c.type === "Vendor"),
      ),
    [contactsResult?.data, editVendorContactId],
  );

  const editFilteredSubcategories = useMemo(
    () =>
      editCategory
        ? subcategoryItems.filter((s) => s.parentValue === editCategory)
        : [],
    [subcategoryItems, editCategory],
  );

  const categoryLabel = useMemo(
    () =>
      expense?.category
        ? (categoryItems.find((c) => c.value === expense.category)?.label ??
            expense.category.replace(/_/g, " "))
        : null,
    [categoryItems, expense?.category],
  );

  const subcategoryLabel = useMemo(
    () =>
      expense?.subcategory
        ? (subcategoryItems.find((s) => s.value === expense.subcategory)
            ?.label ?? expense.subcategory.replace(/_/g, " "))
        : null,
    [subcategoryItems, expense?.subcategory],
  );

  const paymentMethodLabel = useMemo(
    () =>
      expense?.paymentMethod
        ? (paymentItems.find((p) => p.value === expense.paymentMethod)?.label ??
            expense.paymentMethod.replace(/_/g, " "))
        : null,
    [paymentItems, expense?.paymentMethod],
  );

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  function enterEdit() {
    if (!expense) return;
    setEditTitle(expense.title);
    setEditDescription(expense.description ?? "");
    setEditCategory(expense.category ?? "");
    setEditSubcategory(expense.subcategory ?? "");
    setEditPaymentMethod(expense.paymentMethod ?? "");
    setEditDate(expense.expenseDate.slice(0, 10));
    setEditAmount(String(expense.amount));
    setEditCurrency(expense.currency);
    setEditRate(String(expense.exchangeRate));
    setEditReceiptUrl(expense.receiptUrl ?? "");
    setEditVendorCompanyId(expense.vendorCompanyId ?? "");
    setEditNotes(expense.notes ?? "");
    setEditCompanyId(expense.companyId ?? "");
    setEditProductId(expense.productId ?? "");
    setEditProjectId(expense.projectId ?? "");
    setEditPhaseId(expense.phaseId);
    setEditStageId(expense.stageId);
    setEditMeetingId(expense.meetingId ?? "");
    setEditBudgetId(expense.budgetId ?? "");
    setEditAccessVaultId(expense.accessVaultId ?? "");
    setEditAssetId(expense.assetId ?? "");
    setEditVendorContactId(expense.vendorContactId ?? "");
    setEditPaidById(expense.paidById ?? "");
    setEditIsReimbursable(expense.isReimbursable);
    setSaveError(null);
    setEditMode(true);
  }

  async function saveEdit() {
    if (!expense) return;
    const amt = Number(editAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setSaveError("Amount must be a positive number");
      return;
    }
    const rate = Number(editRate) || 1;
    setSaveError(null);
    try {
      await updateExpense.mutateAsync({
        title: editTitle.trim(),
        description: editDescription.trim() || null,
        category: editCategory || null,
        subcategory: editSubcategory || null,
        paymentMethod: editPaymentMethod || null,
        expenseDate: editDate ? new Date(editDate).toISOString() : null,
        amount: amt,
        currency: editCurrency,
        exchangeRate: editCurrency === "AED" ? 1 : rate,
        receiptUrl: editReceiptUrl.trim() || null,
        vendorCompanyId: editVendorCompanyId || null,
        notes: editNotes.trim() || null,
        companyId: editCompanyId || null,
        productId: editProductId || null,
        projectId: editProjectId || null,
        phaseId: editPhaseId,
        stageId: editStageId,
        meetingId: editMeetingId || null,
        budgetId: editBudgetId || null,
        accessVaultId: editAccessVaultId || null,
        assetId: editAssetId || null,
        vendorContactId: editVendorContactId || null,
        paidById: editPaidById || null,
        isReimbursable: editIsReimbursable,
      });
      setEditMode(false);
      showToast("Expense updated");
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save");
    }
  }

  useEffect(() => {
    if (!editMode) return;
    // When clearing parent, clear dependent picks
    if (!editCompanyId && editProductId) setEditProductId("");
    if (!editProjectId) {
      if (editPhaseId) setEditPhaseId(null);
      if (editStageId) setEditStageId(null);
    }
  }, [editMode, editCompanyId, editProductId, editProjectId, editPhaseId, editStageId]);

  async function handleApprove() {
    if (!expense) return;
    await approveExpense.mutateAsync(expense.id);
    showToast("Expense approved");
  }

  async function submitRejection() {
    if (!expense || !rejectReason.trim()) return;
    await rejectExpense.mutateAsync({
      id: expense.id,
      rejectionReason: rejectReason.trim(),
    });
    showToast("Expense rejected");
    setRejectOpen(false);
    setRejectReason("");
  }

  function handleDelete() {
    if (!expense) return;
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    if (!expense) return;
    await deleteExpense.mutateAsync(expense.id);
    navigate("/expenses");
  }

  async function submitComment() {
    if (!commentText.trim()) return;
    await addComment.mutateAsync(commentText.trim());
    setCommentText("");
  }

  if (isLoading || !expense) {
    return (
      <AppLayout
        breadcrumb={[
          { label: "PhazeX Hub", href: "/" },
          { label: "Finance" },
          { label: "Expenses", href: "/expenses" },
        ]}
      >
        <div className="py-12 text-center text-muted-foreground">
          {isLoading ? (
            <>
              <Loader2 className="mx-auto h-6 w-6 animate-spin" strokeWidth={1.5} />
              <div className="mt-2 text-sm">Loading…</div>
            </>
          ) : (
            "Expense not found"
          )}
        </div>
      </AppLayout>
    );
  }

  const pending = expense.status === "pending";
  const rejected = expense.status === "rejected";
  const approved = expense.status === "approved";

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Finance" },
        { label: "Expenses", href: "/expenses" },
        { label: expense.code },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/expenses")}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <Receipt className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
          <h1 className="text-xl font-semibold text-foreground">
            {editMode ? "Edit Expense" : "View Expense"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {!editMode && pending && (
            <>
              <button
                type="button"
                onClick={handleApprove}
                disabled={approveExpense.isPending}
                className="flex h-8 items-center gap-1 rounded-md border px-3 text-[13px] font-medium text-[hsl(142,72%,28%)] hover:bg-[hsl(142,55%,94%)]"
                style={{ borderColor: "hsl(142,55%,75%)" }}
              >
                <Check className="h-4 w-4" strokeWidth={1.8} />
                Approve
              </button>
              <button
                type="button"
                onClick={() => {
                  setRejectOpen(true);
                  setRejectReason("");
                }}
                className="flex h-8 items-center gap-1 rounded-md border px-3 text-[13px] font-medium text-[hsl(0,70%,45%)] hover:bg-[hsl(0,70%,96%)]"
                style={{ borderColor: "hsl(0,70%,80%)" }}
              >
                <X className="h-4 w-4" strokeWidth={1.8} />
                Reject
              </button>
            </>
          )}
          {!editMode && (
            <button
              type="button"
              onClick={() => setActivityOpen(true)}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
              title="History"
            >
              <History className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            </button>
          )}
          {!editMode && (
            <button
              type="button"
              onClick={() => setDuplicateOpen(true)}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
              title="Duplicate this expense"
            >
              <Copy className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            </button>
          )}
          {editMode ? (
            <>
              <button
                type="button"
                onClick={saveEdit}
                disabled={updateExpense.isPending}
                className="flex h-8 items-center gap-1 rounded-md border px-3 text-[13px] font-medium text-[hsl(142,72%,28%)] hover:bg-[hsl(142,55%,94%)]"
                style={{ borderColor: "hsl(142,55%,75%)" }}
              >
                {updateExpense.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                ) : (
                  <Check className="h-4 w-4" strokeWidth={1.8} />
                )}
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditMode(false);
                  setSaveError(null);
                }}
                className="flex h-8 items-center gap-1 rounded-md border px-3 text-[13px] font-medium text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                style={{ borderColor: BORDER }}
              >
                <X className="h-4 w-4" strokeWidth={1.8} />
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={enterEdit}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
              title="Edit"
            >
              <Pencil className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            </button>
          )}
          <button
            type="button"
            onClick={handleDelete}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(0,70%,96%)]"
            title="Delete"
          >
            <Trash2 className="h-4 w-4 text-[hsl(0,70%,45%)]" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* ROW 1 — Summary Header */}
      <div
        className="mb-4 rounded-xl border bg-white"
        style={{ borderColor: BORDER, padding: "16px 20px" }}
      >
        {editMode ? (
          <Input
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            className="text-[20px] font-bold"
          />
        ) : (
          <div className="text-[20px] font-bold text-foreground">
            {expense.title}
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
          <ReferenceIdBadge id={expense.code} />
          <StatusPill status={expense.status} />
          {(categoryLabel || subcategoryLabel) && (
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
              style={{ background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }}
            >
              {categoryLabel}
              {categoryLabel && subcategoryLabel ? " › " : ""}
              {subcategoryLabel}
            </span>
          )}
          {paymentMethodLabel && (
            <span>{paymentMethodLabel}</span>
          )}
          {expense.submittedByName && (
            <span>Submitted by {expense.submittedByName}</span>
          )}
          {expense.paidByName && <span>Paid by {expense.paidByName}</span>}
          <span>{formatDate(expense.expenseDate)}</span>
        </div>

        <div className="mt-4">
          <CurrencyAmount
            amount={expense.amount}
            currency={expense.currency}
            baseAmount={expense.baseAmount}
            baseCurrency={expense.baseCurrency}
            size="lg"
          />
          {expense.currency !== expense.baseCurrency && (
            <div className="mt-1 text-[12px] text-muted-foreground">
              @ {expense.exchangeRate} rate
            </div>
          )}
        </div>

        {rejected && expense.rejectionReason && (
          <div
            className="mt-4 rounded-lg border px-4 py-3 text-sm"
            style={{
              background: "hsl(0,70%,96%)",
              borderColor: "hsl(0,70%,80%)",
              color: "hsl(0,70%,35%)",
            }}
          >
            <span className="font-semibold">Rejected:</span>{" "}
            {expense.rejectionReason}
          </div>
        )}
      </div>

      {/* ROW 2 — Details + Status/Context */}
      <div
        className="mb-4 grid gap-4"
        style={{ gridTemplateColumns: "60fr 40fr" }}
      >
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <div className="mb-3">
            <h3 className="text-[13px] font-semibold text-foreground">
              Expense Details
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Title</div>
              {editMode ? (
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
              ) : (
                <div className="mt-0.5 text-foreground">{expense.title}</div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Code</div>
              <div className="mt-0.5 font-mono text-foreground">{expense.code}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Category</div>
              {editMode ? (
                <Select
                  value={editCategory}
                  onChange={(e) => {
                    setEditCategory(e.target.value);
                    setEditSubcategory("");
                  }}
                >
                  <option value="">Uncategorized</option>
                  {categoryItems.map((c) => (
                    <option key={c.id} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <div className="mt-0.5 text-foreground">
                  {categoryLabel ?? "—"}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Subcategory
              </div>
              {editMode ? (
                <Select
                  value={editSubcategory}
                  onChange={(e) => setEditSubcategory(e.target.value)}
                  disabled={!editCategory}
                >
                  <option value="">
                    {editCategory ? "Select subcategory..." : "Select category first"}
                  </option>
                  {editFilteredSubcategories.map((s) => (
                    <option key={s.id} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <div className="mt-0.5 text-muted-foreground">
                  {subcategoryLabel ?? "—"}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Payment Method
              </div>
              {editMode ? (
                <Select
                  value={editPaymentMethod}
                  onChange={(e) => setEditPaymentMethod(e.target.value)}
                >
                  <option value="">Not specified</option>
                  {paymentItems.map((p) => (
                    <option key={p.id} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <div className="mt-0.5 text-foreground">
                  {paymentMethodLabel ?? "—"}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Expense Date</div>
              {editMode ? (
                <Input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                />
              ) : (
                <div className="mt-0.5 text-foreground">
                  {formatDate(expense.expenseDate)}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Receipt</div>
              {editMode ? (
                <Input
                  value={editReceiptUrl}
                  onChange={(e) => setEditReceiptUrl(e.target.value)}
                />
              ) : expense.receiptUrl ? (
                <a
                  href={expense.receiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-0.5 block truncate text-[hsl(210,100%,35%)] underline-offset-2 hover:underline"
                >
                  View receipt
                </a>
              ) : (
                <div className="mt-0.5 text-muted-foreground">—</div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Amount</div>
              {editMode ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                />
              ) : (
                <div className="mt-0.5 text-foreground">
                  {formatCurrency(expense.amount, expense.currency)}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Currency</div>
              {editMode ? (
                <Input
                  value={editCurrency}
                  onChange={(e) => setEditCurrency(e.target.value)}
                />
              ) : (
                <div className="mt-0.5 text-foreground">{expense.currency}</div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Exchange Rate
              </div>
              {editMode ? (
                <Input
                  type="number"
                  step="0.000001"
                  value={editRate}
                  onChange={(e) => setEditRate(e.target.value)}
                  disabled={editCurrency === "AED"}
                />
              ) : (
                <div className="mt-0.5 text-foreground">
                  {expense.exchangeRate}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Base Amount (AED)
              </div>
              <div className="mt-0.5 font-semibold text-foreground">
                {formatCurrency(expense.baseAmount, expense.baseCurrency)}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Budget</div>
              {editMode ? (
                <select
                  value={editBudgetId}
                  onChange={(e) => setEditBudgetId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No budget</option>
                  {budgets.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.code} — {b.name}
                    </option>
                  ))}
                </select>
              ) : expense.budgetId ? (
                <Link
                  to={`/budgets/${expense.budgetId}`}
                  className="mt-0.5 block text-[hsl(210,100%,35%)] hover:underline"
                >
                  <span className="font-mono text-[11px]">{expense.budgetCode}</span>
                  {expense.budgetName ? ` — ${expense.budgetName}` : ""}
                </Link>
              ) : (
                <div className="mt-0.5 text-muted-foreground">—</div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Paid By</div>
              {editMode ? (
                <select
                  value={editPaidById}
                  onChange={(e) => setEditPaidById(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">Company (no individual payer)</option>
                  {allUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              ) : expense.paidByName ? (
                <div className="mt-0.5 text-foreground">{expense.paidByName}</div>
              ) : (
                <div className="mt-0.5 text-muted-foreground">Company</div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Reimbursable
              </div>
              {editMode ? (
                <label className="mt-1 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editIsReimbursable}
                    onChange={(e) => setEditIsReimbursable(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <span className="text-foreground">
                    This expense is reimbursable
                  </span>
                </label>
              ) : (
                <div className="mt-0.5 text-foreground">
                  {expense.isReimbursable ? "Yes" : "No"}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Vendor (External)
              </div>
              {editMode ? (
                <ExternalCompanyPicker
                  id="exp-edit-vendor"
                  value={editVendorCompanyId}
                  onChange={setEditVendorCompanyId}
                  systemCodes={["vendor"]}
                  placeholder="No vendor"
                />
              ) : expense.vendorCompanyId ? (
                <Link
                  to={`/companies/${expense.vendorCompanyId}`}
                  className="mt-0.5 block text-[hsl(210,100%,35%)] hover:underline"
                >
                  {expense.vendorCompanyName ?? "—"}
                </Link>
              ) : (
                <div className="mt-0.5 text-muted-foreground">—</div>
              )}
            </div>
            <div className="col-span-2">
              <div className="text-[11px] uppercase text-muted-foreground">
                Description
              </div>
              {editMode ? (
                <textarea
                  rows={3}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="mt-1 flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              ) : (
                <div className="mt-0.5 whitespace-pre-wrap text-sm text-foreground">
                  {expense.description ?? "—"}
                </div>
              )}
            </div>
            <div className="col-span-2">
              <div className="text-[11px] uppercase text-muted-foreground">
                Notes
              </div>
              {editMode ? (
                <textarea
                  rows={2}
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  className="mt-1 flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              ) : (
                <div className="mt-0.5 whitespace-pre-wrap text-sm text-foreground">
                  {expense.notes ?? "—"}
                </div>
              )}
            </div>
          </div>

          {editMode && (
            <div className="mt-4 space-y-4 border-t pt-4" style={{ borderColor: "hsl(210,30%,90%)" }}>
              <h4 className="text-[12px] font-semibold uppercase text-muted-foreground">
                Linked Context
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Company</Label>
                  <select
                    value={editCompanyId}
                    onChange={(e) => {
                      setEditCompanyId(e.target.value);
                      setEditProductId("");
                      setEditProjectId("");
                      setEditPhaseId(null);
                      setEditStageId(null);
                    }}
                    className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm"
                  >
                    <option value="">No company</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label>Product</Label>
                  <select
                    value={editProductId}
                    onChange={(e) => {
                      setEditProductId(e.target.value);
                      setEditProjectId("");
                      setEditPhaseId(null);
                      setEditStageId(null);
                    }}
                    className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm"
                  >
                    <option value="">No product</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label>Project</Label>
                  <select
                    value={editProjectId}
                    onChange={(e) => {
                      setEditProjectId(e.target.value);
                      setEditPhaseId(null);
                      setEditStageId(null);
                    }}
                    className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm"
                  >
                    <option value="">No project</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label>Meeting</Label>
                  <select
                    value={editMeetingId}
                    onChange={(e) => setEditMeetingId(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm"
                  >
                    <option value="">No meeting</option>
                    {meetings.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.code ?? ""} — {m.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {editProjectId && (
                <PhaseStagePicker
                  projectId={editProjectId || null}
                  phaseId={editPhaseId}
                  stageId={editStageId}
                  onPhaseChange={setEditPhaseId}
                  onStageChange={setEditStageId}
                />
              )}

              <h4 className="text-[12px] font-semibold uppercase text-muted-foreground">
                Related Entities
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Vault Entry</Label>
                  <select
                    value={editAccessVaultId}
                    onChange={(e) => setEditAccessVaultId(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm"
                  >
                    <option value="">No vault entry</option>
                    {vaultEntries.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.code} — {v.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label>Asset</Label>
                  <select
                    value={editAssetId}
                    onChange={(e) => setEditAssetId(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm"
                  >
                    <option value="">No asset</option>
                    {assets.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} — {a.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2 space-y-1">
                  <Label>Vendor Contact</Label>
                  <select
                    value={editVendorContactId}
                    onChange={(e) => setEditVendorContactId(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm"
                  >
                    <option value="">No vendor contact</option>
                    {vendorContacts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.fullName}
                        {c.companyName ? ` — ${c.companyName}` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-muted-foreground">
                    Only vendor-type external contacts are shown.
                  </p>
                </div>
              </div>
            </div>
          )}

          {editMode && saveError && (
            <div
              className="mt-3 rounded-md border px-3 py-2 text-sm"
              style={{
                borderColor: "hsl(0,70%,80%)",
                background: "hsl(0,70%,96%)",
                color: "hsl(0,70%,35%)",
              }}
            >
              {saveError}
            </div>
          )}
        </div>

        {/* RIGHT — Status & Context */}
        <div className="space-y-4">
          <div
            className="rounded-xl border bg-white"
            style={{ borderColor: BORDER, padding: "16px 20px" }}
          >
            <h3 className="mb-3 text-[13px] font-semibold text-foreground">
              Status
            </h3>
            <StatusPill status={expense.status} />
            <div className="mt-3 text-[12px] text-muted-foreground">
              {approved && expense.approvedByName && (
                <>
                  Approved by{" "}
                  <span className="text-foreground">{expense.approvedByName}</span>{" "}
                  on {formatDateTime(expense.approvedAt)}
                </>
              )}
              {rejected && (
                <>
                  Rejected
                  {expense.approvedByName
                    ? ` by ${expense.approvedByName}`
                    : ""}
                  {expense.rejectionReason && (
                    <div className="mt-2 text-[hsl(0,70%,45%)]">
                      Reason: {expense.rejectionReason}
                    </div>
                  )}
                </>
              )}
              {pending && <>Awaiting approval.</>}
            </div>
          </div>

          <ReimbursementCard expense={expense} />

          <div
            className="rounded-xl border bg-white"
            style={{ borderColor: BORDER, padding: "16px 20px" }}
          >
            <h3 className="mb-3 text-[13px] font-semibold text-foreground">Context</h3>
            <div className="space-y-2 text-[13px]">
              {expense.companyId && (
                <Link
                  to={`/companies/${expense.companyId}`}
                  className="flex items-center gap-2 hover:text-[hsl(210,100%,35%)]"
                >
                  <Building2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {expense.companyName}
                </Link>
              )}
              {expense.productId && (
                <Link
                  to={`/products/${expense.productId}`}
                  className="flex items-center gap-2 hover:text-[hsl(210,100%,35%)]"
                >
                  <Package className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {expense.productName}
                </Link>
              )}
              {expense.projectId && (
                <Link
                  to={`/projects/${expense.projectId}`}
                  className="flex items-center gap-2 hover:text-[hsl(210,100%,35%)]"
                >
                  <FolderKanban className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {expense.projectName}
                </Link>
              )}
              {expense.phaseName && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  Phase: {expense.phaseName}
                </div>
              )}
              {expense.budgetId && (
                <Link
                  to={`/budgets/${expense.budgetId}`}
                  className="flex items-center gap-2 hover:text-[hsl(210,100%,35%)]"
                >
                  <Wallet className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {expense.budgetCode} — {expense.budgetName}
                </Link>
              )}
              {expense.meetingId && (
                <Link
                  to={`/meetings/${expense.meetingId}`}
                  className="flex items-center gap-2 hover:text-[hsl(210,100%,35%)]"
                >
                  <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {expense.meetingTitle}
                </Link>
              )}
              {expense.accessVaultId && (
                <Link
                  to={`/access-vault/${expense.accessVaultId}`}
                  className="flex items-center gap-2 hover:text-[hsl(210,100%,35%)]"
                >
                  <KeyRound className="h-3.5 w-3.5" strokeWidth={1.5} />
                  <span className="font-mono text-[11px]">
                    {expense.accessVaultCode}
                  </span>
                  {expense.accessVaultTitle
                    ? ` — ${expense.accessVaultTitle}`
                    : ""}
                </Link>
              )}
              {expense.assetId && (
                <Link
                  to={`/assets/${expense.assetId}`}
                  className="flex items-center gap-2 hover:text-[hsl(210,100%,35%)]"
                >
                  <Boxes className="h-3.5 w-3.5" strokeWidth={1.5} />
                  <span className="font-mono text-[11px]">
                    {expense.assetCode}
                  </span>
                  {expense.assetName ? ` — ${expense.assetName}` : ""}
                </Link>
              )}
              {expense.vendorContactId && (
                <Link
                  to={`/contacts/${expense.vendorContactId}`}
                  className="flex items-center gap-2 hover:text-[hsl(210,100%,35%)]"
                >
                  <UserSquare2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {expense.vendorContactName ?? "Vendor contact"}
                </Link>
              )}
              {!expense.companyId &&
                !expense.productId &&
                !expense.projectId &&
                !expense.budgetId &&
                !expense.meetingId &&
                !expense.accessVaultId &&
                !expense.assetId &&
                !expense.vendorContactId && (
                  <div className="text-muted-foreground">No context linked</div>
                )}
            </div>

            <div
              className="mt-4 border-t pt-3 text-[11px] text-muted-foreground"
              style={{ borderColor: "hsl(210,30%,90%)" }}
            >
              <div>Created: {formatDateTime(expense.createdAt)}</div>
              <div>Updated: {formatDateTime(expense.updatedAt)}</div>
              {expense.approvedAt && (
                <div>Approved: {formatDateTime(expense.approvedAt)}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ROW 3 — Comments + Attachments tabs */}
      <div
        className="overflow-hidden rounded-xl border bg-white"
        style={{ borderColor: BORDER }}
      >
        <div
          className="flex items-center border-b"
          style={{ borderColor: "hsl(210,30%,90%)" }}
        >
          {[
            { key: "comments", label: "Comments", count: commentsQuery.data?.length ?? 0 },
            { key: "attachments", label: "Attachments", count: 0 },
          ].map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key as "comments" | "attachments")}
                className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground"
                }`}
              >
                {t.label}
                <span
                  className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium"
                  style={{
                    background: active ? "hsl(210,55%,92%)" : "hsl(210,16%,93%)",
                    color: active ? "hsl(210,100%,28%)" : "hsl(215,16%,40%)",
                  }}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {tab === "comments" && (
            <div className="flex flex-col">
              <div className="mb-3 flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-foreground" strokeWidth={1.5} />
                <h3 className="text-[13px] font-semibold text-foreground">
                  Comments
                </h3>
              </div>

              <div className="max-h-[400px] flex-1 overflow-y-auto">
                {(!commentsQuery.data || commentsQuery.data.length === 0) && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No comments yet. Be the first to comment.
                  </div>
                )}
                {[...(commentsQuery.data ?? [])].reverse().map((c) => {
                  const isOwn = c.authorId === user?.id;
                  return (
                    <div
                      key={c.id}
                      className="border-b py-3 last:border-b-0"
                      style={{ borderColor: "hsl(210,30%,93%)" }}
                    >
                      <div className="flex items-start gap-2">
                        <span
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                          style={{
                            background: "hsl(210,55%,92%)",
                            color: "hsl(210,100%,28%)",
                          }}
                        >
                          {initialsOf(c.authorName ?? "??")}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[13px] font-semibold text-foreground">
                              {c.authorName ?? "Unknown"}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {timeAgo(c.createdAt)}
                            </span>
                            {isOwn && (
                              <button
                                type="button"
                                onClick={() => deleteComment.mutate(c.id)}
                                className="ml-auto text-muted-foreground hover:text-[hsl(0,70%,45%)]"
                                title="Delete"
                              >
                                <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                              </button>
                            )}
                          </div>
                          <div className="mt-1 whitespace-pre-wrap text-[14px] text-foreground">
                            {c.content}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                className="mt-3 flex items-end gap-2 border-t pt-3"
                style={{ borderColor: "hsl(210,30%,90%)" }}
              >
                <textarea
                  rows={2}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.ctrlKey && e.key === "Enter") {
                      e.preventDefault();
                      submitComment();
                    }
                  }}
                  placeholder="Write a comment... (Ctrl+Enter to submit)"
                  className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={submitComment}
                  disabled={!commentText.trim() || addComment.isPending}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-white disabled:opacity-50"
                  style={{ background: "hsl(210,100%,45%)" }}
                  title="Send"
                >
                  <Send className="h-3.5 w-3.5" strokeWidth={1.8} />
                </button>
              </div>
            </div>
          )}
          {tab === "attachments" && (
            <FileUploadZone
              entityType="expense"
              entityId={expense.id}
              entityLabel={expense.code}
              expenseId={expense.id}
              budgetId={expense.budgetId ?? undefined}
              companyId={expense.companyId ?? undefined}
              productId={expense.productId ?? undefined}
              projectId={expense.projectId ?? undefined}
              meetingId={expense.meetingId ?? undefined}
              showExisting
            />
          )}
        </div>
      </div>

      {rejectOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
          <div
            className="w-[440px] rounded-xl border bg-white p-6 shadow-xl"
            style={{ borderColor: BORDER }}
          >
            <h3 className="text-[15px] font-semibold text-foreground">
              Reject Expense
            </h3>
            <p className="mt-1 text-[12px] text-muted-foreground">
              {expense.code} — {expense.title}
            </p>
            <div className="mt-4 space-y-2">
              <label className="text-[12px] font-medium">
                Reason for rejection <span className="text-destructive">*</span>
              </label>
              <textarea
                rows={4}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder="Explain why this expense is being rejected..."
                autoFocus
              />
            </div>
            <div className="mt-4 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setRejectOpen(false);
                  setRejectReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={submitRejection}
                disabled={!rejectReason.trim() || rejectExpense.isPending}
                className="bg-[hsl(0,70%,50%)] text-white hover:bg-[hsl(0,70%,42%)]"
              >
                Reject Expense
              </Button>
            </div>
          </div>
        </div>
      )}

      <NewExpenseDrawer
        isOpen={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        duplicateFrom={expense}
        onCreated={(code) => showToast(`Expense created: ${code}`)}
      />

      <ActivityHistoryDrawer
        isOpen={activityOpen}
        onClose={() => setActivityOpen(false)}
        entityType="expense"
        entityId={expense.id}
        entityLabel={expense.title}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete expense?"
        description={`Are you sure you want to delete '${expense.title}'? It will be removed from your Expenses list.`}
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}
